import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { BreadcrumbsTable } from "../../../components/common/HelperFunc";

const CYCLE_OPTIONS = [
  {
    value: "monthly",
    label: "매월",
    months: 1,
  },
  {
    value: "quarterly",
    label: "분기",
    months: 3,
  },
  {
    value: "half_year",
    label: "반기",
    months: 6,
  },
  {
    value: "yearly",
    label: "연간",
    months: 12,
  },
  {
    value: "custom_month",
    label: "직접 입력",
    months: null,
  },
];

const DEFAULT_FORM = {
  cycle_type: "monthly",
  cycle_months: 1,
  base_day: 1,
  remind_days_before: 7,
};

const pageContainerSx = {
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  p: { xs: "16px", md: "16px 32px" },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  bgcolor: "var(--mui-palette-background-paper)",
};

const panelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "0.375rem",
  bgcolor: "#fff",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
};

const toInt = (value, fallback) => {
  const next = Number(value);
  return Number.isFinite(next) ? Math.trunc(next) : fallback;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pad2 = (value) => String(value).padStart(2, "0");

const getCycleOption = (type) =>
  CYCLE_OPTIONS.find((option) => option.value === type) || CYCLE_OPTIONS[0];

const normalizeForm = (source = {}) => {
  const option = getCycleOption(source.cycle_type);
  const cycleMonths =
    option.months ||
    clamp(toInt(source.cycle_months, DEFAULT_FORM.cycle_months), 1, 60);

  return {
    cycle_type: option.value,
    cycle_months: cycleMonths,
    base_day: clamp(toInt(source.base_day, DEFAULT_FORM.base_day), 1, 28),
    remind_days_before: clamp(
      toInt(source.remind_days_before, DEFAULT_FORM.remind_days_before),
      0,
      60
    ),
  };
};

const buildPreviewDates = (form) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const cycleMonths = Math.max(1, Number(form.cycle_months || 1));
  const baseDay = clamp(Number(form.base_day || 1), 1, 28);
  const dates = [];

  for (let offset = 0; offset < 36 && dates.length < 6; offset += 1) {
    const monthIndex = currentMonth + offset;
    if (monthIndex % cycleMonths !== 0) continue;

    const date = new Date(currentYear, monthIndex, baseDay);
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    if (date < todayDate) continue;

    dates.push(
      `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
        date.getDate()
      )}`
    );
  }

  return dates;
};

export function StandardEvalCycleSetting() {
  const { group_code, project_code } = useParams();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(DEFAULT_FORM);

  const apiBase = `/risk/${group_code}/${project_code}/eval-cycle`;
  const queryKey = ["risk-eval-cycle-setting", group_code, project_code];

  const settingQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await axiosInstance.get(apiBase);
      return response.data?.setting || DEFAULT_FORM;
    },
    enabled: Boolean(group_code && project_code),
  });

  useEffect(() => {
    if (settingQuery.data) {
      setForm(normalizeForm(settingQuery.data));
    }
  }, [settingQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axiosInstance.put(apiBase, payload);
      return response.data?.setting;
    },
    onSuccess: (setting) => {
      queryClient.setQueryData(queryKey, setting);
      setForm(normalizeForm(setting));
      toast.success("위험성평가 주기 설정이 저장되었습니다.");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "위험성평가 주기 설정 저장 중 오류가 발생했습니다."
      );
    },
  });

  const previewDates = useMemo(() => buildPreviewDates(form), [form]);

  const handleCycleTypeChange = (event) => {
    const option = getCycleOption(event.target.value);
    setForm((prev) => ({
      ...prev,
      cycle_type: option.value,
      cycle_months: option.months || prev.cycle_months || 1,
    }));
  };

  const handleNumberChange = (key, min, max) => (event) => {
    const value = clamp(toInt(event.target.value, form[key]), min, max);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    saveMutation.mutate(normalizeForm(form));
  };

  const handleReset = () => {
    setForm(normalizeForm(settingQuery.data || DEFAULT_FORM));
  };

  return (
    <Box sx={pageContainerSx}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            minHeight: 46,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              위험성평가 주기 설정
            </Typography>
            {settingQuery.isFetching ? <CircularProgress size={16} /> : null}
          </Stack>
          <BreadcrumbsTable
            breadcrumbs={["기준정보", "위험성평가 주기 설정"]}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              minHeight: 62,
              px: { xs: 2, md: 2.5 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              주기 정보
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={saveMutation.isPending}
              >
                되돌리기
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                저장
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>평가 주기</InputLabel>
                      <Select
                        label="평가 주기"
                        value={form.cycle_type}
                        onChange={handleCycleTypeChange}
                      >
                        {CYCLE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="개월 간격"
                      type="number"
                      value={form.cycle_months}
                      onChange={handleNumberChange("cycle_months", 1, 60)}
                      disabled={form.cycle_type !== "custom_month"}
                      inputProps={{ min: 1, max: 60 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="기준일"
                      type="number"
                      value={form.base_day}
                      onChange={handleNumberChange("base_day", 1, 28)}
                      inputProps={{ min: 1, max: 28 }}
                      helperText="월마다 안정적으로 계산되도록 1일부터 28일까지 사용합니다."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="사전 알림"
                      type="number"
                      value={form.remind_days_before}
                      onChange={handleNumberChange("remind_days_before", 0, 60)}
                      inputProps={{ min: 0, max: 60 }}
                      helperText="평가 예정일 며칠 전부터 알림에 표시할지 설정합니다."
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography sx={{ fontWeight: 900 }}>예정일 미리보기</Typography>
                  </Box>
                  <Stack
                    spacing={0}
                    divider={<Divider />}
                    sx={{
                      maxHeight: 320,
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                      scrollbarColor: "#cbd5e1 transparent",
                      "&::-webkit-scrollbar": { width: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#cbd5e1",
                        borderRadius: 8,
                      },
                    }}
                  >
                    {previewDates.map((date, index) => (
                      <Stack
                        key={date}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ minHeight: 42, px: 1.5 }}
                      >
                        <Typography sx={{ fontWeight: index === 0 ? 900 : 700 }}>
                          {date}
                        </Typography>
                        <Chip
                          size="small"
                          label={index === 0 ? "다음 예정" : `${index + 1}번째`}
                          color={index === 0 ? "primary" : "default"}
                          variant={index === 0 ? "filled" : "outlined"}
                          sx={{ fontWeight: 800 }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
